import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, BookOpen, Brain, Award, CheckCircle, Target, TrendingUp, Shield } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import VittaRSEPathways from '../components/VittaRSEPathways';

const RSEExam = () => {
  const rseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "RSE Exam Prep Course - Retail Securities Exam",
    "description": "Comprehensive preparation for the Retail Securities Exam (RSE). Master KYC, KYP, suitability, investment products, portfolio construction, and compliance.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "url": "https://www.vittafinance.ca"
    },
    "courseCode": "RSE-PREP",
    "educationalLevel": "Professional",
    "about": ["Retail Securities Exam", "Investment Advisor Certification", "Client-facing investment roles"],
    "teaches": ["KYC/KYP", "Suitability assessment", "Investment products", "Portfolio construction", "Taxation", "Compliance"],
    "numberOfCredits": "120 questions in 3 hours",
    "offers": {
      "@type": "Offer",
      "price": "29.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    }
  };

  const rseFaqData = [
    {
      question: "What is the RSE exam?",
      answer: "The Retail Securities Exam (RSE) is a CIRO proficiency exam for investment professionals who want to work with retail clients—individual investors and small businesses. It covers KYC, KYP, suitability, investment products, and portfolio construction."
    },
    {
      question: "How many questions are on the RSE exam?",
      answer: "The RSE exam has 120 multiple-choice questions that must be completed in 3 hours (180 minutes). The passing score is determined by CIRO for each exam sitting."
    },
    {
      question: "What is the difference between RSE and ISE?",
      answer: "The RSE (Retail Securities Exam) is for professionals working with individual/retail clients, while the ISE (Institutional Securities Exam) is for those working with institutional clients like corporations and pension funds. RSE focuses more on client relationships and suitability."
    },
    {
      question: "What careers can I pursue after passing the RSE?",
      answer: "After passing the RSE, you can work as an Investment Advisor, Financial Planner, Wealth Manager, or Retail Broker. These roles involve advising individual clients on investment decisions."
    },
    {
      question: "How long should I study for the RSE exam?",
      answer: "We recommend 6-8 weeks of dedicated study time for the RSE exam. The exam is more comprehensive than the CIRE, so allow extra time for topics like portfolio construction and suitability assessment."
    },
    {
      question: "Do I need to pass CIRE before taking RSE?",
      answer: "While not always mandatory, completing the CIRE exam before RSE is recommended as it provides foundational knowledge that helps with RSE preparation."
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="RSE Exam Prep 2025 - Retail Securities Exam Course | CIRO Certified"
        description="Pass the Retail Securities Exam (RSE) with Canada's top prep course. 120 questions, 3 hours exam. Master KYC, KYP, suitability, portfolio construction & compliance. Expert video lessons, AI tutor, practice questions, mock exams. Perfect for retail investment advisors. Industry leading pass rate."
        keywords="RSE exam, Retail Securities Exam, CIRO RSE, RSE syllabus, RSE practice exam, RSE sample questions, RSE study guide, Retail securities exam prep, RSE exam format, RSE exam fees, RSE mock exam, RSE question bank, RSE notes, RSE pass score, RSE exam dates, RSE 30 day study plan, RSE vs ISE, investment advisor exam Canada, retail advisor certification, KYC KYP exam, portfolio construction exam, RSE exam prep Canada, RSE exam prep Alberta, RSE exam prep Ontario, RSE exam prep Toronto, CIRO proficiency requirements investment representative"
        ogTitle="Retail Securities Exam (RSE) Prep Course - Pass Your CIRO Certification"
        ogDescription="Complete RSE exam preparation for retail investment advisors. Expert instruction, 800+ practice questions, full mock exams. Industry leading pass rate. Join 30,000+ successful candidates."
        structuredData={rseStructuredData}
        faqData={rseFaqData}
        pageType="course"
      />

      {/* Hero Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Courses', link: '/courses' },
            { label: 'RSE Exam' }
          ]} />
          
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            Retail Securities Exam (RSE)
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            maxWidth: '800px',
            opacity: 0.95
          }}>
            Master the RSE exam for retail investment advisors. Comprehensive preparation covering KYC, KYP, suitability, products, and compliance.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <a href="https://vittafinance.mykajabi.com/offers/2kzQbJim" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Enroll Now <ArrowRight size={20} />
            </a>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: 'var(--brand-primary)',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{
        padding: '40px 0',
        backgroundColor: 'var(--neutral-light)',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '30px',
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '8px' }}>120</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Questions</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '8px' }}>3 Hours</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Exam Duration</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)', marginBottom: '8px' }}>9</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Core Elements</div>
            </div>
          </div>
        </div>
      </section>

      {/* About RSE Exam */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>About the Retail Securities Exam</h2>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              The <strong>Retail Securities Exam (RSE)</strong> is the core proficiency requirement for client-facing investment roles in Canada, including advisors who provide product recommendations and suitability assessments.
            </p>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              The exam evaluates applied competency across <strong>KYC (Know Your Client)</strong>, <strong>KYP (Know Your Product)</strong>, client suitability, investment products, portfolio construction, taxation, compliance, and ethical decision-making.
            </p>
          </div>
        </div>
      </section>

      {/* Key Topics */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>RSE Exam Key Topics</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {[
              { icon: <Target size={32} />, title: 'KYC & Suitability', desc: 'Client profiling, risk assessment, and suitability determination' },
              { icon: <TrendingUp size={32} />, title: 'Investment Products', desc: 'Equities, fixed income, mutual funds, ETFs, and alternatives' },
              { icon: <Shield size={32} />, title: 'Portfolio Construction', desc: 'Asset allocation, diversification, and rebalancing strategies' },
              { icon: <BookOpen size={32} />, title: 'Taxation', desc: 'Canadian tax implications for investment products' },
              { icon: <Award size={32} />, title: 'Compliance & Ethics', desc: 'Regulatory requirements and professional conduct' },
              { icon: <Video size={32} />, title: 'Product Analysis', desc: 'Fundamental and technical analysis methods' }
            ].map((topic, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                <div style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>{topic.icon}</div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{topic.title}</h3>
                <p className="body-small">{topic.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Pathways */}
      <VittaRSEPathways />

      {/* Study Materials */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>RSE Study Materials on Amazon</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'RSE Interactive Quick Study Guide',
                price: '49.99',
                originalPrice: '129.99',
                features: '9 Elements + Mock Exam',
                link: '/books/rse-interactive',
                isInternal: true,
                desc: 'Master RSE with 9 elements, reading notes, games, flashcards, quizzes, and 120-question mock exam',
                badge: 'New! April Special'
              },
              {
                title: 'RSE Mock Exam Pack (Online)',
                price: '49.99',
                features: '5 Mock Exams',
                link: '/books/rse-mock-exam-pack',
                isInternal: true,
                desc: '5 complete RSE mock exams with 550 questions and comprehensive rationales',
                badge: 'New!'
              },
              {
                title: 'RSE Element-Wise Question Bank (Online)',
                price: '49.99',
                features: '9 Element Banks',
                link: '/books/rse-element-bank',
                isInternal: true,
                desc: '9 comprehensive question banks with detailed rationales for focused element-wise practice',
                badge: 'New!'
              },
              {
                title: 'Printed RSE Study Guide',
                price: '299.99',
                pages: '780',
                link: 'https://www.amazon.ca/dp/B0G3L3TCJR',
                desc: 'Comprehensive study guide with case-driven methodology'
              },
              {
                title: 'RSE 5 Full-Length Mock Exams',
                price: '49.99',
                questions: '600+',
                link: 'https://www.amazon.ca/dp/B0GG6TS3DQ',
                desc: 'Realistic practice exams mirroring RSE structure'
              },
              {
                title: 'RSE Quick Study Guide',
                price: '34.99',
                concepts: '300',
                link: 'https://www.amazon.ca/dp/B0GC55S3HZ',
                desc: 'Top 300 concepts for fast final review'
              }
            ].map((product, idx) => (
              <div key={idx} style={{
                backgroundColor: 'var(--neutral-light)',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }} className="card-hover-effect">
                {product.badge && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    backgroundColor: '#22d3ee',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {product.badge}
                  </div>
                )}
                <h3 className="heading-5" style={{ marginBottom: '16px' }}>{product.title}</h3>
                <div style={{ marginBottom: '16px' }}>
                  {product.pages && <span className="caption" style={{ backgroundColor: 'white', padding: '6px 12px', borderRadius: '4px', marginRight: '8px' }}>{product.pages} Pages</span>}
                  {product.questions && <span className="caption" style={{ backgroundColor: 'white', padding: '6px 12px', borderRadius: '4px', marginRight: '8px' }}>{product.questions} Questions</span>}
                  {product.concepts && <span className="caption" style={{ backgroundColor: 'white', padding: '6px 12px', borderRadius: '4px', marginRight: '8px' }}>Top {product.concepts} Concepts</span>}
                  {product.features && <span className="caption" style={{ backgroundColor: 'white', padding: '6px 12px', borderRadius: '4px' }}>{product.features}</span>}
                </div>
                <p className="body-small" style={{ marginBottom: '24px', flex: 1 }}>{product.desc}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  {product.originalPrice && (
                    <span style={{ fontSize: '1.2rem', color: '#999', textDecoration: 'line-through' }}>
                      CAD ${product.originalPrice}
                    </span>
                  )}
                  <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--brand-primary)' }}>
                    CAD ${product.price}
                  </div>
                </div>
                {product.isInternal ? (
                  <a href={product.link} className="btn-primary" style={{ width: '100%', textDecoration: 'none', textAlign: 'center' }}>View Interactive Guide</a>
                ) : (
                  <a href={product.link} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ width: '100%' }}>Buy on Amazon</a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>RSE Online Course Pricing</h2>
          <div style={{
            maxWidth: '450px',
            margin: '0 auto'
          }}>
            <div style={{
              backgroundColor: 'var(--brand-primary)',
              padding: '48px',
              borderRadius: '16px',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 12px 40px rgba(30, 58, 138, 0.25)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-14px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--gold-primary)',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              }}>BEST VALUE</div>
              <h3 className="heading-4" style={{ marginBottom: '16px', color: 'white' }}>Monthly Plan</h3>
              <div style={{ fontSize: '3.5rem', fontWeight: 900, color: 'var(--gold-primary)', marginBottom: '8px' }}>$29.99</div>
              <p className="body-small" style={{ marginBottom: '32px', color: 'rgba(255,255,255,0.8)' }}>per month • Cancel anytime</p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '32px', textAlign: 'left' }}>
                {['All video lessons', 'AI Tutor access', 'Practice questions', 'Mock exams', 'Study guides'].map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <CheckCircle size={22} color="var(--gold-primary)" />
                    <span className="body-small" style={{ color: 'white', fontSize: '1rem' }}>{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="https://vittafinance.mykajabi.com/offers/2kzQbJim" target="_blank" rel="noopener noreferrer" className="btn-gold" style={{ width: '100%', padding: '16px 32px', fontSize: '1.1rem' }}>Get Started</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 48px'
          }}>
            Everything you need to know about the RSE exam
          </p>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {rseFaqData.map((faq, idx) => (
              <details 
                key={idx}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <summary style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: 'var(--text-primary)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {faq.question}
                  <span style={{ 
                    fontSize: '1.5rem', 
                    color: 'var(--brand-primary)',
                    fontWeight: 400
                  }}>+</span>
                </summary>
                <div style={{
                  padding: '0 24px 20px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>Start Your RSE Exam Preparation Today</h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Join thousands of successful retail investment advisors who passed with Vitta Finance Academy
          </p>
          <a href="https://www.vittafinance.ca/store" target="_blank" rel="noopener noreferrer" className="btn-gold">
            Enroll in RSE Course <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default RSEExam;
